import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  acceptAll,
  applyConsent,
  getStoredConsent,
  hasDecided,
  rejectAll,
} from "@/utils/consent";

export const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  useEffect(() => {
    if (!hasDecided()) {
      setOpen(true);
    }
    const stored = getStoredConsent();
    if (stored) {
      setAnalytics(stored.analytics);
      setMarketing(stored.marketing);
    }

    // Omogući ponovno otvaranje iz footera ("Podešavanja kolačića").
    const reopen = () => {
      const s = getStoredConsent();
      if (s) {
        setAnalytics(s.analytics);
        setMarketing(s.marketing);
      }
      setShowSettings(true);
      setOpen(true);
    };
    window.addEventListener("open-cookie-settings", reopen);
    return () => window.removeEventListener("open-cookie-settings", reopen);
  }, []);

  if (!open) return null;

  const handleAcceptAll = () => {
    acceptAll();
    setOpen(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setOpen(false);
  };

  const handleSavePreferences = () => {
    applyConsent({ analytics, marketing });
    setOpen(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4">
      <div className="mx-auto max-w-3xl rounded-xl border border-border bg-background shadow-2xl">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Cookie size={20} />
            </div>
            <div className="flex-1">
              <h3 className="font-serif text-base font-semibold text-foreground">
                Kolačići na ovom sajtu
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground font-ui">
                Koristimo neophodne kolačiće za rad sajta, a uz vaš pristanak i
                kolačiće za analitiku i oglašavanje (Google Ads). Više informacija
                u{" "}
                <Link
                  to="/politika-privatnosti"
                  className="text-primary underline underline-offset-2 hover:text-gold"
                >
                  Politici privatnosti
                </Link>
                .
              </p>

              {showSettings && (
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">Neophodni</p>
                      <p className="text-xs text-muted-foreground">
                        Potrebni za rad sajta i korpu. Uvijek aktivni.
                      </p>
                    </div>
                    <Switch checked disabled />
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">Analitika</p>
                      <p className="text-xs text-muted-foreground">
                        Mjerenje posjeta i ponašanja na sajtu (PostHog).
                      </p>
                    </div>
                    <Switch checked={analytics} onCheckedChange={setAnalytics} />
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">Marketing</p>
                      <p className="text-xs text-muted-foreground">
                        Oglašavanje i mjerenje konverzija (Google Ads).
                      </p>
                    </div>
                    <Switch checked={marketing} onCheckedChange={setMarketing} />
                  </div>
                </div>
              )}

              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <Button onClick={handleAcceptAll} className="sm:order-3">
                  Prihvati sve
                </Button>
                {showSettings ? (
                  <Button variant="secondary" onClick={handleSavePreferences} className="sm:order-2">
                    Sačuvaj izbor
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setShowSettings(true)}
                    className="sm:order-2"
                  >
                    Podešavanja
                  </Button>
                )}
                <Button variant="ghost" onClick={handleRejectAll} className="sm:order-1 sm:mr-auto">
                  Odbij sve
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
